/* eslint-disable sonarjs/max-switch-cases */
import { Reducer } from 'redux';
import { CheckoutActions, CheckoutActionTypes, CheckoutState } from './checkout.types';

const initialState: CheckoutState = {
    isCheckoutPageInitializing: false,
    isPaymentPageInitializing: false,
    isPaymentPageInitialized: false,
    isOrderSummaryPageInitializing: false,
    isDeliveryDetailsLoading: false,
    deliveryDetails: null,
    isDeliveryDetailsUpdating: false,
    deliveryDetailsUpdatingErrors: null,
    isUpdatingEca: false,
    deliverNotBefore: null,
    selectedQuickestDeliveryDate: false,
    isCheckingEmail: false,
    userExists: null,
    userEmail: null,
    isAuthProcess: false,
    authErrors: null,
    credentialErrors: null,
    isOrderProcessing: false,
    isLookingForLastOrder: false,
    isOrderPlacing: false,
    processOrderError: null,
    order: null,
    firstFinanceApplicationPageInitialized: false,
    addressSearchEditors: {},
    summaryPageError: null,
    isSubmitFinanceApplicationProcessing: false,
    submitFinanceApplicationError: null,
    isCompleteResetPasswordPageInitializing: false,
    completeResetPasswordError: null,
    ifcQuestionSet: null,
    ifcApplicationData: null,
    isIfcApplicationFilledByCustomer: false,
    shouldShowFinanceApplicationNotValidWarning: false,
    isContinueFromDeliveryDateProcessing: false
};

export const checkoutReducer: Reducer<CheckoutState, CheckoutActions> = (state = initialState, action) => {
    switch (action.type) {

        case CheckoutActionTypes.INIT_CHECKOUT_PAGE_START: {
            return { ...state, isCheckoutPageInitializing: true };
        }

        case CheckoutActionTypes.INIT_CHECKOUT_PAGE_SUCCESS:
        case CheckoutActionTypes.INIT_CHECKOUT_PAGE_ERROR: {
            return { ...state, isCheckoutPageInitializing: false };
        }

        case CheckoutActionTypes.INIT_ORDER_SUMMARY_PAGE_START: {
            return { ...state, isOrderSummaryPageInitializing: true };
        }

        case CheckoutActionTypes.INIT_ORDER_SUMMARY_PAGE_SUCCESS: {
            return { ...state, isOrderSummaryPageInitializing: false, order: action.order };
        }

        case CheckoutActionTypes.INIT_ORDER_SUMMARY_PAGE_ERROR: {
            return { ...state, isOrderSummaryPageInitializing: false, summaryPageError: action.error };
        }

        case CheckoutActionTypes.LOAD_DELIVERY_DETAILS_START: {
            return { ...state, isDeliveryDetailsLoading: true, deliveryDetails: null };
        }

        case CheckoutActionTypes.LOAD_DELIVERY_DETAILS_SUCCESS: {
            return { ...state, deliveryDetails: action.deliveryDetails, isDeliveryDetailsLoading: false };
        }

        case CheckoutActionTypes.LOAD_DELIVERY_DETAILS_ERROR: {
            return { ...state, isDeliveryDetailsLoading: false };
        }

        case CheckoutActionTypes.UPDATE_DELIVERY_DETAILS_START: {
            return { ...state, isDeliveryDetailsUpdating: true, deliveryDetailsUpdatingErrors: null };
        }

        case CheckoutActionTypes.UPDATE_DELIVERY_DETAILS_SUCCESS: {
            return { ...state, isDeliveryDetailsUpdating: false, deliveryDetailsUpdatingErrors: null };
        }

        case CheckoutActionTypes.UPDATE_DELIVERY_DETAILS_ERROR: {
            return { ...state, isDeliveryDetailsUpdating: false, deliveryDetailsUpdatingErrors: action.errors };
        }

        /** loader is not used right now */
        case CheckoutActionTypes.SET_DELIVER_NOT_BEFORE_START: {
            return { ...state, isUpdatingEca: true };
        }

        case CheckoutActionTypes.SET_DELIVER_NOT_BEFORE_SUCCESS: {
            return { ...state, isUpdatingEca: false, deliverNotBefore: action.date, selectedQuickestDeliveryDate: action.isQuickestDate };
        }

        case CheckoutActionTypes.SET_DELIVER_NOT_BEFORE_ERROR: {
            return { ...state, isUpdatingEca: false };
        }

        case CheckoutActionTypes.CLEAR_DELIVER_NOT_BEFORE: {
            return { ...state, deliverNotBefore: null, isContinueFromDeliveryDateProcessing: false };
        }

        case CheckoutActionTypes.CHECK_EMAIL_START: {
            return { ...state, isCheckingEmail: true };
        }

        case CheckoutActionTypes.CHECK_EMAIL_SUCCESS: {
            return { ...state, isCheckingEmail: false, userExists: action.userExists, userEmail: action.email };
        }

        case CheckoutActionTypes.CHECK_EMAIL_ERROR: {
            return { ...state, isCheckingEmail: false };
        }

        case CheckoutActionTypes.CLEAR_EMAIL: {
            return { ...state, userExists: null, userEmail: null };
        }

        case CheckoutActionTypes.LOGIN_PROCESS_START: {
            return { ...state, isAuthProcess: true };
        }

        case CheckoutActionTypes.LOGIN_PROCESS_SUCCESS: {
            return { ...state, isAuthProcess: false, authErrors: null };
        }

        case CheckoutActionTypes.LOGIN_PROCESS_ERROR: {
            return { ...state, isAuthProcess: false, authErrors: action.error };
        }

        case CheckoutActionTypes.LOGIN_CREDENTIALS_ERROR: {
            return { ...state, isAuthProcess: false, credentialErrors: action.error };
        }

        case CheckoutActionTypes.CLEAR_LOGIN_AUTH_ERRORS: {
            return { ...state, authErrors: null, credentialErrors: null, userExists: null, userEmail: null };
        }

        case CheckoutActionTypes.REGISTER_PROCESS_START: {
            return { ...state, isAuthProcess: true };
        }

        case CheckoutActionTypes.REGISTER_PROCESS_SUCCESS: {
            return { ...state, isAuthProcess: false, authErrors: null };
        }

        case CheckoutActionTypes.REGISTER_PROCESS_ERROR: {
            return { ...state, isAuthProcess: false, authErrors: action.error };
        }

        case CheckoutActionTypes.PROCESS_ORDER_START: {
            return { ...state, isOrderProcessing: true };
        }

        case CheckoutActionTypes.PROCESS_ORDER_SUCCESS: {
            return { ...state, isOrderProcessing: false, processOrderError: null };
        }

        case CheckoutActionTypes.PROCESS_ORDER_ERROR: {
            return { ...state, isOrderProcessing: false, processOrderError: action.error };
        }

        case CheckoutActionTypes.FIND_LAST_ORDER_START: {
            return { ...state, isLookingForLastOrder: true };
        }

        case CheckoutActionTypes.FIND_LAST_ORDER_SUCCESS:
        case CheckoutActionTypes.FIND_LAST_ORDER_ERROR: {
            return { ...state, isLookingForLastOrder: false };
        }

        case CheckoutActionTypes.PLACE_ORDER_START: {
            return { ...state, isOrderPlacing: true };
        }

        case CheckoutActionTypes.PLACE_ORDER_SUCCESS:
        case CheckoutActionTypes.PLACE_ORDER_ERROR: {
            return { ...state, isOrderPlacing: false };
        }

        case CheckoutActionTypes.INIT_FIRST_FINANCE_APPLICATION_PAGE_START: {
            return { ...state, firstFinanceApplicationPageInitialized: false };
        }

        case CheckoutActionTypes.INIT_FIRST_FINANCE_APPLICATION_PAGE_SUCCESS: {
            return { ...state, firstFinanceApplicationPageInitialized: true };
        }

        case CheckoutActionTypes.SAVE_IFC_FINANCE_DATA_SUCCESS: {
            return { ...state, ifcApplicationData: action.ifcApplicationData, isIfcApplicationFilledByCustomer: action.ifcApplicationFilledByCustomer };
        }

        case CheckoutActionTypes.INIT_ADDRESS_SEARCH_EDITOR: {
            return {
                ...state, addressSearchEditors: {
                    ...state.addressSearchEditors,
                    [action.addressSearchEditorId]: {
                        ...state.addressSearchEditors[action.addressSearchEditorId],
                        isFindAddressProcessing: false,
                        addressFoundButCannotBeParsed: false
                    }
                }
            };
        }

        case CheckoutActionTypes.CLEAR_FINANCE_DATA: {
            return {
                ...state,
                ifcApplicationData: null
            };
        }

        case CheckoutActionTypes.REMOVE_ADDRESS_SEARCH_EDITOR: {
            return {
                ...state,
                addressSearchEditors: {
                    ...state.addressSearchEditors,
                    [action.addressSearchEditorId]: undefined
                }
            };
        }

        case CheckoutActionTypes.FIND_ADDRESS_START: {
            return {
                ...state,
                addressSearchEditors: {
                    ...state.addressSearchEditors,
                    [action.addressSearchEditorId]: {
                        ...state.addressSearchEditors[action.addressSearchEditorId]!,
                        isFindAddressProcessing: true
                    }
                }
            };
        }

        case CheckoutActionTypes.FIND_ADDRESS_SUCCESS: {
            return {
                ...state,
                addressSearchEditors: {
                    ...state.addressSearchEditors,
                    [action.addressSearchEditorId]: {
                        ...state.addressSearchEditors[action.addressSearchEditorId],
                        foundAddresses: action.foundAddresses,
                        isFindAddressProcessing: false,
                        addressFoundButCannotBeParsed: action.isSuccessfulSearchButAddressCannotBeParsed
                    }
                }
            };
        }

        case CheckoutActionTypes.FIND_ADDRESS_ERROR: {
            return {
                ...state,
                addressSearchEditors: {
                    ...state.addressSearchEditors,
                    [action.addressSearchEditorId]: {
                        ...state.addressSearchEditors[action.addressSearchEditorId],
                        foundAddresses: undefined,
                        isFindAddressProcessing: false,
                        addressFoundButCannotBeParsed: false
                    }
                }
            };
        }

        case CheckoutActionTypes.INIT_PAYMENT_PAGE_START: {
            return {
                ...state,
                isPaymentPageInitializing: true,
                isPaymentPageInitialized: false,
            };
        }

        case CheckoutActionTypes.INIT_PAYMENT_PAGE_SUCCESS: {
            return {
                ...state,
                isPaymentPageInitializing: false,
                isPaymentPageInitialized: true,
            };
        }

        case CheckoutActionTypes.INIT_PAYMENT_PAGE_ERROR: {
            return {
                ...state,
                isPaymentPageInitializing: false,
                isPaymentPageInitialized: false
            };
        }

        case CheckoutActionTypes.INIT_COMPLETE_RESET_PASSWORD_PAGE_START: {
            return { ...state, isCompleteResetPasswordPageInitializing: true };
        }

        case CheckoutActionTypes.INIT_COMPLETE_RESET_PASSWORD_PAGE_SUCCESS: {
            return { ...state, isCompleteResetPasswordPageInitializing: false };
        }

        case CheckoutActionTypes.INIT_COMPLETE_RESET_PASSWORD_PAGE_ERROR: {
            return { ...state, isCompleteResetPasswordPageInitializing: false, completeResetPasswordError: action.error };
        }

        case CheckoutActionTypes.LOAD_QUESTION_SET_SUCCESS: {
            return { ...state, ifcQuestionSet: action.questionSet };
        }

        case CheckoutActionTypes.INIT_IFC_APPLICATION_DATA_SUCCESS: {
            return { ...state, ifcApplicationData: action.ifcApplicationData };
        }

        case CheckoutActionTypes.SET_SHOW_FINANCE_APPLICATION_NOT_VALID_WARNING: {
            return { ...state, shouldShowFinanceApplicationNotValidWarning: action.shouldShowFinanceApplicationNotValidWarning };
        }

        case CheckoutActionTypes.CONTINUE_FROM_DELIVERY_DATE_START: {
            return { ...state, isContinueFromDeliveryDateProcessing: true };
        }

        case CheckoutActionTypes.CONTINUE_FROM_DELIVERY_DATE_SUCCESS:
        case CheckoutActionTypes.CONTINUE_FROM_DELIVERY_DATE_ERROR: {
            return { ...state, isContinueFromDeliveryDateProcessing: false };
        }

        default: {
            return state;
        }
    }
};
